<template>
    <v-card class="app-content-card elevation-5">
        <v-card-title class="py-2">
            <span>{{ title }}</span>
            <help-button :href="helpUrl"></help-button>
        </v-card-title>
        <v-card-text>
            <v-container fluid class="px-4 py-0">
                <v-row no-gutters>
                    <v-col>
                        <div class="text-subtitle-2">Support number</div>
                        <action-link-button
                            :value="selectedVenueInfo.supportNumber"
                            :prepend-icon="icons.phone"
                            color="accent"
                            link-type="tel"
                            class="px-0"
                        ></action-link-button>
                    </v-col>
                    <v-col>
                        <div class="text-subtitle-2">Billing account Id</div>
                        <div class="text-subtitle-1 font-weight-bold pt-2">
                            {{ selectedVenueInfo.billingAccountId }}
                        </div>
                    </v-col>
                </v-row>
                <v-row no-gutters>
                    <v-col class="py-1">
                        <div class="text-subtitle-2">Documents</div>
                    </v-col>
                </v-row>
            </v-container>
            <v-simple-table fixed-headers>
                <template v-slot:default>
                    <tbody>
                        <tr v-if="dataLoading || documentsPending">
                            <td colspan="10" class="pt-4">
                                <v-progress-linear
                                    indeterminate
                                    rounded
                                    height="4"
                                ></v-progress-linear>
                                <div
                                    class="py-5 text-body-1 font-weight-medium text-center grey--text"
                                >
                                    Loading... Please wait
                                </div>
                            </td>
                        </tr>
                        <tr v-else-if="noDataFound">
                            <td colspan="10">
                                <div
                                    class="text-body-1 font-weight-medium text-center grey--text"
                                >
                                    No data found...
                                </div>
                            </td>
                        </tr>
                        <tr
                            v-for="(item, index) in visibleDocuments"
                            :key="`doc-${index}`"
                        >
                            <td>
                                <v-tooltip top>
                                    <template v-slot:activator="{ on }">
                                        <v-btn
                                            text
                                            color="accent"
                                            v-on="on"
                                            @click="onDocumentClick(item)"
                                        >
                                            <v-icon class="mr-2">{{
                                                icons.file
                                            }}</v-icon>
                                            <span>{{ item.name }}</span>
                                        </v-btn>
                                    </template>
                                    <div>
                                        <div>Key: {{ item.Key }}</div>
                                        <div>
                                            Last modified:
                                            {{ item.LastModified }}
                                        </div>
                                        <div>Size: {{ item.Size }}</div>
                                    </div>
                                </v-tooltip>
                            </td>
                            <td class="text-right">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on }">
                                        <v-btn
                                            v-if="showDeleteButton"
                                            text
                                            color="accent"
                                            cy-data="delete-row"
                                            v-on="on"
                                            @click="onDeleteDocument(item)"
                                        >
                                            delete
                                        </v-btn>
                                    </template>
                                    <span
                                        >Delete the document permanently!</span
                                    >
                                </v-tooltip>
                            </td>
                        </tr>
                    </tbody>
                </template>
            </v-simple-table>
            <v-tooltip top>
                <template v-slot:activator="{ on }">
                    <v-btn
                        v-show="showAddButton"
                        fab
                        fixed
                        right
                        bottom
                        small
                        color="primary"
                        class="mb-5 mr-1"
                        aria-label="Upload new document"
                        cy-data="add-row"
                        v-on="on"
                        @click="onAddDocumentClick"
                    >
                        <v-icon>{{ icons.add }}</v-icon>
                    </v-btn>
                </template>
                <span>Upload new document</span>
            </v-tooltip>
        </v-card-text>
        <confirmation-dialog ref="confirmationDialog"></confirmation-dialog>
        <upload-dialog
            :show="showDialog"
            @close="onUploadDialogClose"
        ></upload-dialog>
    </v-card>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import { isEmpty } from '@/common/helpers';
import ConfirmationDialog from '@/components/Dialogs/ConfirmationPromiseDialog';
import ActionLinkButton from '@/components/Fields/ActionLinkButton';
import UploadDialog from './UploadFileDialog';
import { sortBy } from 'lodash';
import HelpButton from '@/components/Buttons/HelpButton';

export default {
    name: 'SupportPage',
    components: {
        'confirmation-dialog': ConfirmationDialog,
        'upload-dialog': UploadDialog,
        'action-link-button': ActionLinkButton,
        'help-button': HelpButton
    },
    data() {
        return {
            title: 'Customer Support',
            showDialog: false,
            lastFocusedElement: null,
            dataLoading: true
        };
    },
    computed: {
        ...mapState('support', [
            'documents',
            'buttonsConfig',
            'documentsPending',
            'prefixes',
            'helpUrl'
        ]),
        ...mapState('venue', ['selectedVenueInfo']),
        ...mapGetters('venue', [
            'currentMso',
            'currentPartner',
            'currentVenue'
        ]),
        showDeleteButton() {
            return this.$can('show', 'Buttons/support/deleteDocument');
        },
        showAddButton() {
            return this.$can('show', 'Buttons/support/addDocument');
        },
        noDataFound() {
            if (this.visibleDocuments && this.visibleDocuments.length) {
                return false;
            }
            return true;
        },
        visibleDocuments() {
            if (this.documents && this.documents.length) {
                const filtered = this.documents
                    .filter(item => {
                        const parts = item.Key.split('/');
                        return (
                            !item.Key.endsWith('/') &&
                            ((parts.includes(this.currentMso) &&
                                parts.includes('shared')) ||
                                (parts.includes(this.currentPartner) &&
                                    parts.includes('shared')) ||
                                parts.includes(this.currentVenue))
                        );
                    })
                    .map(item => {
                        return {
                            ...item,
                            name: item.Key.split('/')
                                .slice(-1)[0]
                                .toLowerCase(),
                            prefix: item.Key.split('/')
                                .slice(0, -1)
                                .join('/')
                        };
                    });
                return sortBy(filtered, 'name');
            }
            return [];
        },
        venueBillingAccountId() {
            if (
                this.selectedVenueInfo &&
                this.selectedVenueInfo.billingAccountId
            ) {
                return this.selectedVenueInfo.billingAccountId;
            }
            return '';
        }
    },
    created() {
        if (isEmpty(this.buttonsConfig)) {
            this.setupView();
        }
        if (isEmpty(this.prefixes)) {
            this.fetchPrefixes();
        }
        this.refreshData();
    },
    methods: {
        ...mapActions('support', [
            'fetchDocuments',
            'fetchDocumentByKey',
            'fetchPrefixes',
            'deleteDocument',
            'createDocument',
            'setupView'
        ]),
        // =================== Events ========================
        async onDeleteDocument(item) {
            const res = await this.$refs.confirmationDialog.open(
                'Delete Document',
                'Are you sure you want to delete this document?',
                'Once deleted, there is no way to revert this action',
                { showIcon: true }
            );
            if (res) {
                await this.deleteDocument({ key: item.Key });
                this.refreshData();
            }
        },
        async onDocumentClick(item) {
            console.log('Open document', item.Key);
            console.time('file download');
            const res = await this.fetchDocumentByKey({ key: item.Key });
            console.timeEnd('file download');
            if (res && res.data) {
                const type = res.headers['content-type'] || 'application/pdf';
                this.downloadFile(res.data, item.Key, type);
            }
        },
        onAddDocumentClick(event) {
            this.showDialog = true;
            this.lastFocusedElement = event.target;
        },
        onUploadDialogClose(refresh) {
            this.showDialog = false;
            if (refresh) {
                this.$nextTick(() => {
                    this.refreshData();
                });
            }
            this.setNextFocus(this.lastFocusedElement);
        },
        // ================ Others ===========================
        async refreshData() {
            this.dataLoading = true;
            await this.fetchDocuments().finally(() => {
                this.dataLoading = false;
            });
        },
        downloadFile(data, filename, type) {
            var newBlob = new Blob([data], { type: type });

            // IE doesn't allow using a blob object directly as link href
            // instead it is necessary to use msSaveOrOpenBlob
            if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                window.navigator.msSaveOrOpenBlob(newBlob);
                return;
            }

            // For other browsers:
            // Create a link pointing to the ObjectURL containing the blob.
            const url = window.URL.createObjectURL(newBlob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('target', '_blank');
            // link.setAttribute('download', filename);
            // if (typeof link.download === 'undefined') {
            //     link.setAttribute('target', '_blank');
            // }
            link.click();
            setTimeout(() => {
                // For Firefox it is necessary to delay revoking the ObjectURL
                window.URL.revokeObjectURL(data);
            }, 100);
        },
        setNextFocus(sourceElement) {
            const keydownEsc = new KeyboardEvent('keydown', { keyCode: 27 });
            this.$nextTick(() => {
                if (sourceElement) {
                    const button = sourceElement.closest('button');
                    button.focus();
                    button.dispatchEvent(keydownEsc);
                }
            });
        }
    }
};
</script>

<style lang="scss" scoped></style>
